import { useEffect, useState } from "react";
import { AlertTriangle, Camera } from "lucide-react";
import api from "../api";
import { useProctor } from "../hooks/useProctor";

/**
 * SalesCertProctorHud — one-line drop-in that wires continuous proctoring for any
 * Sales Cert section page. Renders a small camera preview + warning flashes in a
 * fixed corner overlay. Fetches the assignment_id from /salescert/status.
 *
 * IMPORTANT — Sales Cert governance:
 *   • Flags are LOGGED + FLASHED to the candidate but never hard-block.
 *   • The candidate keeps going regardless of warning count.
 *   • VI reviews the compiled per-participant integrity report in
 *     /admin/integrity → Sales Cert 2026 tab and decides re-attempts post-hoc.
 *   • This is intentional and differs from the COMPASS flow's auto-submit behaviour.
 */
export default function SalesCertProctorHud() {
  const [assignmentId, setAssignmentId] = useState(null);

  useEffect(() => {
    api.get("/participant/salescert/status")
      .then((r) => setAssignmentId(r.data.assignment_id))
      .catch(() => { /* proctor is best-effort; never break the section */ });
  }, []);

  // We intentionally destructure autoSubmit but do NOT act on it — flag-only policy.
  // eslint-disable-next-line no-unused-vars
  const { warnings, lastFlash, videoRef, canvasRef, autoSubmit } = useProctor({
    assignmentId,
    enabled: !!assignmentId,
    frameIntervalSec: 15,
  });

  if (!assignmentId) return null;

  return (
    <>
      {/* Warning flash — top-center */}
      {lastFlash && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 bg-[#EE2737] text-white px-4 py-2 text-xs uppercase tracking-widest font-bold z-50 shadow-lg flex items-center gap-2"
          data-testid="salescert-proctor-flash"
        >
          <AlertTriangle size={14} /> {lastFlash}
        </div>
      )}

      {/* Camera preview HIDDEN by product decision — the video element must
          still exist so useProctor's captureFrame() can grab still frames
          every 15s, but it's positioned off-screen so it doesn't distract the
          candidate. The warning counter tile is kept visible in the corner. */}
      <div className="fixed bottom-6 right-6 z-40 bg-[#0A0A0A] border-2 border-[#E5E7EB] shadow-xl px-3 py-2" data-testid="salescert-proctor-hud">
        <div className="flex items-center gap-2 text-white text-[10px] uppercase tracking-widest font-bold">
          <Camera size={12} className="text-[#EE2737]" /> Proctoring active
          {warnings > 0 && (
            <span className="text-[#F59E0B] ml-2">· {warnings} warn{warnings > 1 ? "s" : ""}</span>
          )}
        </div>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="sr-only"
          style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
        />
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </>
  );
}
